import React, { useState, useContext } from 'react';
import { ChevronDown } from 'lucide-react';

// 旧版本的 Select (用于向后兼容)
interface LegacySelectProps {
  value: string;
  onChange: (value: string) => void;
  options: { value: string; label: string }[];
  placeholder?: string;
  className?: string;
}

export function LegacySelect({ value, onChange, options, placeholder = '请选择', className = '' }: LegacySelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const selectedOption = options.find(opt => opt.value === value);

  return (
    <div className={`relative ${className}`}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-4 py-2 text-left bg-muted border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent flex items-center justify-between text-foreground"
      >
        <span className={value ? '' : 'text-muted-foreground'}>
          {selectedOption?.label || placeholder}
        </span>
        <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <>
          <div className="fixed inset-0 z-[9998]" onClick={() => setIsOpen(false)} />
          <div className="absolute z-[9999] w-full mt-1 bg-popover border border-border rounded-lg shadow-lg max-h-60 overflow-auto">
            {options.map(option => (
              <button
                key={option.value}
                type="button"
                onClick={() => {
                  onChange(option.value);
                  setIsOpen(false);
                }}
                className={`w-full px-4 py-2 text-left hover:bg-accent transition-colors ${
                  option.value === value ? 'bg-primary/10 text-primary' : 'text-popover-foreground'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// 新的 shadcn/ui 风格组件
interface SelectContextType {
  value: string;
  onValueChange: (value: string) => void;
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  itemLabels: React.MutableRefObject<Record<string, string>>;
}

const SelectContext = React.createContext<SelectContextType | null>(null);

interface SelectProps {
  value: string;
  onValueChange: (value: string) => void;
  children: React.ReactNode;
  labels?: Record<string, string>;
}

export function Select({ value, onValueChange, children, labels }: SelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const itemLabels = React.useRef<Record<string, string>>({});

  return (
    <SelectContext.Provider value={{ value, onValueChange, isOpen, setIsOpen, itemLabels }}>
      <div className="relative">
        {children}
      </div>
    </SelectContext.Provider>
  );
}

export function SelectTrigger({ children, className = '', disabled = false }: { children: React.ReactNode; className?: string; disabled?: boolean }) {
  const context = useContext(SelectContext);
  if (!context) throw new Error('SelectTrigger must be used within Select');

  return (
    <button
      type="button"
      onClick={() => !disabled && context.setIsOpen(!context.isOpen)}
      disabled={disabled}
      className={`w-full px-3 py-2 text-left bg-muted border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary flex items-center justify-between text-foreground ${disabled ? 'cursor-not-allowed opacity-50' : ''} ${className}`}
    >
      {children}
      <ChevronDown className={`h-4 w-4 text-muted-foreground ml-2 ${context.isOpen ? 'rotate-180' : ''}`} />
    </button>
  );
}

export function SelectValue({ placeholder = '请选择' }: { placeholder?: string }) {
  const context = useContext(SelectContext);
  if (!context) throw new Error('SelectValue must be used within Select');

  // 从 itemLabels ref 中获取对应的 label
  const displayValue = context.itemLabels.current[context.value] || context.value;

  return (
    <span className={!context.value ? 'text-muted-foreground' : 'truncate'}>
      {displayValue || placeholder}
    </span>
  );
}

export function SelectContent({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  const context = useContext(SelectContext);
  if (!context) throw new Error('SelectContent must be used within Select');

  if (!context.isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 z-[9998]" onClick={() => context.setIsOpen(false)} />
      <div className={`absolute z-[9999] w-full mt-1 bg-popover border border-border rounded-lg shadow-lg max-h-60 overflow-auto ${className}`}>
        {children}
      </div>
    </>
  );
}

export function SelectItem({ value, children, className = '' }: { value: string; children: React.ReactNode; className?: string }) {
  const context = useContext(SelectContext);
  if (!context) throw new Error('SelectItem must be used within Select');

  // 注册 label 到 context 的 ref 中
  const label = typeof children === 'string' ? children : String(children);
  context.itemLabels.current[value] = label;

  const handleClick = React.useCallback(() => {
    context.onValueChange(value);
    context.setIsOpen(false);
  }, [context, value]);

  return (
    <button
      type="button"
      onClick={handleClick}
      className={`w-full px-3 py-2 text-left hover:bg-accent transition-colors ${
        context.value === value ? 'bg-primary/10 text-primary' : 'text-popover-foreground'
      } ${className}`}
    >
      {children}
    </button>
  );
}

